import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concat,
  delay,
  filter,
  forkJoin,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  AttributeService,
  EmailService,
  GroupCategoryService,
  GroupService,
  TreeService,
  UserService,
} from '../../shared';
import {
  CreateUsersResponse,
  EditGroup,
  EmailList,
  FlattenedTreeModel,
  OrganisationGroupModel,
  User,
  ValidationStatusEnum,
} from '../../shared/models';
import {
  OrganisationEmailSetting,
  OrganisationEmailTrigger,
} from '../../shared/models/company/company.model';
import { SegmentService } from '../../shared/services/segment.service';
import { ToasterService } from '../../shared/services/toaster.service';
import * as OrganizationActions from '../actions/organization.actions';
import { LoadUserInfo } from '../actions/user-info.actions';
import { AppState } from '../app.state';

@Injectable()
export class OrganizationEffects {
  private store = inject<Store<AppState>>(Store);
  private actions$ = inject(Actions);
  private userService = inject(UserService);
  private treeService = inject(TreeService);
  private attributeService = inject(AttributeService);
  private groupService = inject(GroupService);
  private groupCategoryService = inject(GroupCategoryService);
  private emailService = inject(EmailService);
  private segmentService = inject(SegmentService);
  private snackBar = inject(MatSnackBar);
  private toaster = inject(ToasterService);
  private translations = inject(TranslocoService);

  private readonly productContext = this.store.selectSignal(
    (s) => s.companyContext?.productContext,
  );
  private readonly communicationSentStatuses = this.store.selectSignal(
    (s) => s.organizationState.communicationSentStatuses,
  );

  /** The enabled `OrganisationEmailSetting` configured for a trigger on the current product context, if any. */
  private findOrganisationEmailSetting(
    trigger: OrganisationEmailTrigger,
  ): OrganisationEmailSetting | undefined {
    return this.productContext()?.emails?.find((e) => e.trigger === trigger && e.enabled !== false);
  }

  /**
   * Looks up the concrete company email to send for an organisation-view trigger (e.g. a manager
   * or a member being added to a team), per the current product context's `emails` config. Emits
   * `undefined` if the context has no enabled setting for this trigger, or no matching company email.
   */
  private findOrganisationEmail$(
    trigger: OrganisationEmailTrigger,
    companyId: number,
  ): Observable<EmailList | undefined> {
    const setting = this.findOrganisationEmailSetting(trigger);
    if (!setting) return of(undefined);

    return this.emailService
      .listWithDefaultActiveEmails(companyId)
      .pipe(
        map((emails) =>
          emails.find(
            (e) =>
              e.typeName === setting.type &&
              (setting.audience == null || e.audience === setting.audience) &&
              (setting.key == null || e.bodyModuleKey === setting.key),
          ),
        ),
      );
  }

  /** Whether a recipient may still receive a given trigger's email — false once they already have it, unless the setting opts in via `allowResend`. */
  private canSendOrganisationEmail(setting: OrganisationEmailSetting, userId: number): boolean {
    if (setting.allowResend === true) return true;
    return !(this.communicationSentStatuses()[userId] ?? []).includes(setting.type);
  }

  private showSomethingWrong(): void {
    this.snackBar.open(this.translations.translate('INSIGHT.GLOBAL.ERROR.SOMETHINGWRONG'), '', {
      duration: 3000,
      politeness: 'polite',
      panelClass: 'polite',
    });
  }

  fetchUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUsers),
      switchMap((action) =>
        this.userService.getByCompanyId(action.companyId).pipe(
          map((users) => {
            return OrganizationActions.fetchUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchUsersError({ error }))),
        ),
      ),
    ),
  );

  fetchUserAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUsersSuccess),
      switchMap((action) =>
        this.userService.getUserAttributesByCompanyId().pipe(
          map((userAttributes) => {
            return OrganizationActions.fetchUserAttributesSuccess({ userAttributes });
          }),
          catchError((error) => of(OrganizationActions.fetchUserAttributesError({ error }))),
        ),
      ),
    ),
  );

  fetchGroupUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroupUsers),
      switchMap((action) =>
        this.userService.getGroupUsers(action.companyId, action.groupId).pipe(
          map((users) => {
            return OrganizationActions.fetchUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchUsersError({ error }))),
        ),
      ),
    ),
  );

  fetchManagersUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchManagersUsers),
      switchMap((action) =>
        this.userService.getUsersByManager(action.userId).pipe(
          map((users) => {
            return OrganizationActions.fetchManagersUsersSuccess({ users });
          }),
          catchError((error) => of(OrganizationActions.fetchManagersUsersError({ error }))),
        ),
      ),
    ),
  );

  // This effect is a bit special. This will probably only be usef for segments.
  // It fetches a group, mostly to get the new user count. And return/dispatches editGroupSuccess
  // Added manager info, even if not needed, state should not be changed for manager.....
  fetchGroupOrSegment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroup),
      withLatestFrom(
        this.store.select((state) => state.organizationState.tree),
        this.store.select((state) => state.organizationState.activeHierarchy),
        this.store.select((state) => state.organizationState.users),
      ),
      switchMap(([action, tree, selectedHierarchyId, users]) => {
        const hierarchy = tree.find((t) => t.id === selectedHierarchyId);
        const flattenedTree = hierarchy?.flattenedTree ?? [];
        const defaultHierarchy = tree.find((t) => t.isDefault);
        const flatTree =
          flattenedTree.length > 0 ? flattenedTree : (defaultHierarchy?.flattenedTree ?? []);

        const stateGroup = this.findGroupById(flatTree, action.groupId);

        return this.groupService.get(action.groupId).pipe(
          switchMap((group) => {
            const existsInTree = !!stateGroup;

            const user = Object.values(users).find(
              (u) => u.externalId === stateGroup?.managerExternalId,
            );
            const manager = user ? { id: user.id, name: user.fullName, email: user.email } : null;

            const mainAction = existsInTree
              ? OrganizationActions.editGroupSuccess({
                  group,
                  manager,
                  currentManagerId: manager?.id,
                })
              : OrganizationActions.addGroupSuccess({
                  hierarchyId: selectedHierarchyId,
                  categoryId: group.categoryId,
                  leaf: true,
                  group,
                  parentId: 0,
                });

            // Always update the group’s users
            const updateUsers$ = this.userService
              .getGroupUsers(action.companyId, action.groupId)
              .pipe(
                map((groupUsers) =>
                  OrganizationActions.updateGroupIdsAndUserIds({
                    hierarchyId: selectedHierarchyId,
                    groupIdsAndUserIds: {
                      [action.groupId]: groupUsers.map((u) => u.id),
                    },
                  }),
                ),
              );

            return merge(of(mainAction), updateUsers$);
          }),
          catchError((error) => of(OrganizationActions.fetchGroupError({ error }))),
        );
      }),
    ),
  );

  private findGroupById(
    tree: FlattenedTreeModel<OrganisationGroupModel>[],
    targetId: number,
  ): FlattenedTreeModel<OrganisationGroupModel> | undefined {
    for (const node of tree) {
      if (node.id === targetId) {
        return node;
      }

      if (Array.isArray(node.children)) {
        const result = this.findGroupById(node.children, targetId);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  fetchTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchTree),
      switchMap((action) =>
        this.treeService.getOrganisationTree(action.companyId).pipe(
          map((tree) => {
            return OrganizationActions.fetchTreeSuccess({ tree });
          }),
          catchError((error) => of(OrganizationActions.fetchTreeError({ error }))),
        ),
      ),
    ),
  );

  fetchGroupIdsAndUserIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchGroupIdsAndUserIds),
      switchMap((action) =>
        this.userService.getGroupsUserIds(action.hierarchyId).pipe(
          map((groupIdsAndUserIds) => {
            return OrganizationActions.fetchGroupIdsAndUserIdsSuccess({
              hierarchyId: action.hierarchyId,
              groupIdsAndUserIds,
            });
          }),
          catchError((error) => of(OrganizationActions.fetchGroupIdsAndUserIdsError({ error }))),
        ),
      ),
    ),
  );

  fetchUserIdsForGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchUserIdsForGroup),
      switchMap((action) =>
        this.userService.getGroupUserIds(action.groupId).pipe(
          map((hierarchyAndUserIds) => {
            return OrganizationActions.fetchUserIdsForGroupSuccess({
              hierarchyId: hierarchyAndUserIds.hierarchyId,
              groupId: action.groupId,
              userIds: hierarchyAndUserIds.userIds,
            });
          }),
        ),
      ),
    ),
  );

  fetchCompanyAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchCompanyAttributes),
      switchMap((action) =>
        this.attributeService.getByCompany(action.companyId).pipe(
          map((attributes) => {
            return OrganizationActions.fetchCompanyAttributesSuccess({ attributes });
          }),
          catchError((error) => of(OrganizationActions.fetchCompanyAttributesError({ error }))),
        ),
      ),
    ),
  );

  validateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.validateUser),
      switchMap((action) =>
        this.userService
          .validateManuallyEditedUser({
            companyId: action.user.companyId,
            email: action.user.email,
            externalId: action.user.externalId,
            id: action.user.id,
            phoneNumber: action.user.phoneNumber,
          })
          .pipe(
            mergeMap((fields) => {
              if (fields.length === 0) {
                const followUpAction = action.user.id
                  ? OrganizationActions.editUser({ user: action.user })
                  : OrganizationActions.addUser({ user: action.user });

                return [OrganizationActions.validateUserSuccess({ fields }), followUpAction];
              } else {
                return [OrganizationActions.validateUserFailed({ fields })];
              }
            }),
            catchError((error) => of(OrganizationActions.validateUserError({ error }))),
          ),
      ),
    ),
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addUser),
      switchMap((action) =>
        this.userService.create(action.user).pipe(
          map((user) => {
            user.hierarchyId = action.user.hierarchyId;
            user.groupId = action.user.groupId;
            return OrganizationActions.addUserSuccess({ user });
          }),
          catchError((error) => of(OrganizationActions.addUserError({ error }))),
        ),
      ),
    ),
  );

  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editUser),
      switchMap((action) =>
        this.userService.update(action.user.id, action.user).pipe(
          map((user) => {
            return OrganizationActions.editUserSuccess({ user });
          }),
          catchError((error) => of(OrganizationActions.editUserError({ error }))),
        ),
      ),
    ),
  );

  addGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addGroup),
      switchMap((action) =>
        this.groupService.createSubGroup(action.parentGroupId, action.group).pipe(
          mergeMap((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;

            return [
              OrganizationActions.addGroupSuccess({
                hierarchyId: action.group.hierarchyId,
                categoryId: action.group.categoryId,
                parentId: action.parentGroupId,
                group: createdGroup,
                leaf: action.leaf,
                manager: action.manager,
              }),
            ];
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.addGroupValidationFail({
                  error: error?.error?.error,
                }),
              );
            }

            return of(OrganizationActions.addGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  addRuleBasedGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addRuledBasedGroup),
      switchMap((action) =>
        this.groupService.createRuleBasedSubGroup(action.parentGroupId, action.group).pipe(
          map((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;

            return OrganizationActions.addGroupSuccess({
              hierarchyId: action.hierarchyId,
              categoryId: action.group.categoryId,
              parentId: action.parentGroupId,
              group: createdGroup,
              leaf: action.leaf,
              manager: action.manager,
            });
          }),
          catchError((error) => {
            if (error?.error?.error === 'A group with the selected external ID already exists.') {
              return of(
                OrganizationActions.addGroupValidationFail({
                  error: error?.error?.error,
                }),
              );
            }

            return of(OrganizationActions.addGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  addGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addGroupSuccess),
      map(({ parentId, group }) =>
        OrganizationActions.selectRows({
          ids: [parentId, group.id],
        }),
      ),
    ),
  );

  editRuleBasedGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editRuledBasedGroup),
      switchMap((action) =>
        this.groupService.updateRuleBasedGroup(action.group.id, action.group).pipe(
          map((groupHttpResponse) => {
            return OrganizationActions.editGroupSuccess({
              group: groupHttpResponse.body,
              manager: action.manager,
              currentManagerId: action.currentManagerId,
            });
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.editGroupValidationFail({ error: error?.error?.error }),
              );
            }

            return of(OrganizationActions.editGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  editGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editGroup),
      mergeMap((action) =>
        this.groupService.update(action.group.id, action.group).pipe(
          map((groupHttpResponse) => {
            return OrganizationActions.editGroupSuccess({
              group: groupHttpResponse.body,
              manager: action.manager,
              currentManagerId: action.currentManagerId,
            });
          }),
          catchError((error) => {
            if (
              error?.error?.error ===
              'ExternalId already used by another group of the same category'
            ) {
              return of(
                OrganizationActions.editGroupValidationFail({ error: error?.error?.error }),
              );
            }

            this.showSomethingWrong();
            return of(OrganizationActions.editGroupError({ error }));
          }),
        ),
      ),
    ),
  );

  removeMembersFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.removeMembersFromTeam),
      mergeMap((action) =>
        this.userService
          .removeGroupAttributes({
            groupId: action.groupId,
            userIds: action.userIds,
            hierarchyId: action.hierarchyId,
          })
          .pipe(
            map((users) => OrganizationActions.removeMembersFromTeamSuccess({ users })),
            catchError((error) => {
              this.showSomethingWrong();
              return of(OrganizationActions.removeMembersFromTeamError({ error }));
            }),
          ),
      ),
    ),
  );

  editGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editGroupSuccess),
      withLatestFrom(this.store.select((state) => state.userInfo.userId)),
      filter(([action, userId]) => {
        const newManager = action.manager?.id;
        const oldManager = action.currentManagerId;

        const becameManager = newManager === userId && oldManager !== userId;
        const lostManager = oldManager === userId && newManager !== userId;

        return becameManager || lostManager;
      }),
      map(([action, userId]) => OrganizationActions.fetchManagersUsers({ userId })),
    ),
  );

  refreshManagersUsersAfterAddToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.addUsersToGroup),
      withLatestFrom(this.store.select((state) => state.userInfo.userId)),
      filter(([, userId]) => userId != null),
      map(([, userId]) => OrganizationActions.fetchManagersUsers({ userId: userId! })),
    ),
  );

  moveGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveGroup),
      switchMap((action) =>
        this.groupService
          .moveGroup(action.groupId, {
            hierarchyId: action.hierarchyId,
            currentParentId: action.currentParentId,
            newParentId: action.newParentId,
          })
          .pipe(
            mergeMap(() => [
              OrganizationActions.moveGroupSuccess({
                groupId: action.groupId,
                hierarchyId: action.hierarchyId,
                currentParentId: action.currentParentId,
                newParentId: action.newParentId,
              }),
              OrganizationActions.selectRows({
                ids: [action.newParentId, action.groupId],
              }),
            ]),
            catchError((error) => of(OrganizationActions.moveGroupError({ error }))),
          ),
      ),
    ),
  );

  moveGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveGroups),
      switchMap((action) =>
        this.groupService.moveGroups(action.request).pipe(
          switchMap(() =>
            concat(
              of(
                OrganizationActions.moveGroupsSuccess({
                  companyId: action.companyId,
                  request: action.request,
                }),
              ),
              // First action dispatched immediately
              of(OrganizationActions.fetchTree({ companyId: action.companyId })),

              // Second action dispatched 500ms later so the tree component have time to render
              of(
                OrganizationActions.selectRows({
                  ids: [
                    action.request.moveGroupInfo[0]?.moveGroup?.newParentId,
                    ...action.request.moveGroupInfo.map((g) => g.groupId),
                  ],
                }),
              ).pipe(delay(500)),
            ),
          ),
          catchError((error) => of(OrganizationActions.moveGroupError({ error }))),
        ),
      ),
    ),
  );

  fetchAllowedMoveCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.fetchAllowedMoveCategories),
      switchMap((action) =>
        this.groupCategoryService.getAllowedMoveCategories(action.companyId).pipe(
          map((allowedCategories) => {
            return OrganizationActions.fetchAllowedMoveCategoriesSuccess({
              allowedMoveCategories: allowedCategories,
            });
          }),
          catchError((error) => {
            return of(OrganizationActions.fetchAllowedMoveCategoriesError({ error }));
          }),
        ),
      ),
    ),
  );

  moveUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.moveUsers),
      switchMap((action) =>
        this.userService
          .move({
            companyId: action.companyId,
            hierarchyId: action.hierarchyId,
            newGroupId: action.newGroupId,
            userIds: action.userIds,
          })
          .pipe(
            mergeMap((users) => [
              OrganizationActions.moveUsersSuccess({
                hierarchyId: action.hierarchyId,
                groupId: action.newGroupId,
                userIds: action.userIds,
                usersAfterMove: users,
              }),
              OrganizationActions.selectRows({
                ids: [action.newGroupId, ...action.userIds],
              }),
            ]),
            catchError((error) => {
              this.toaster.error(
                this.translations.translate('INSIGHT.GLOBAL.ERROR.SOMETHINGWRONG'),
              );
              return of(OrganizationActions.moveUsersError({ error }));
            }),
          ),
      ),
    ),
  );

  deleteSegment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteSegment),
      switchMap((action) =>
        this.segmentService.deleteSegment({ id: action.groupId }).pipe(
          map((_) => {
            return OrganizationActions.deleteGroupSuccess({ groupId: action.groupId });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteGroupError({ groupId: action.groupId, error }));
          }),
        ),
      ),
    ),
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteGroup),
      mergeMap((action) =>
        this.groupService.delete(action.groupId).pipe(
          map((_) => {
            return OrganizationActions.deleteGroupSuccess({ groupId: action.groupId });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteGroupError({ groupId: action.groupId, error }));
          }),
        ),
      ),
    ),
  );

  deleteTeamAndRemoveAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteTeamAndRemoveAttributes),
      withLatestFrom(this.store.select((state) => state.companyContext.id)),
      mergeMap(([action, companyId]) => {
        // 1. Remove group attributes from all team members
        const removeAttributes$ =
          action.userIds.length > 0
            ? this.userService.removeGroupAttributes({
                groupId: action.groupId,
                userIds: action.userIds,
                hierarchyId: action.hierarchyId,
              })
            : of([] as any[]);

        return removeAttributes$.pipe(
          // 2. Delete the group
          switchMap((updatedUsers) =>
            this.groupService.delete(action.groupId).pipe(
              mergeMap(() => {
                const resultActions: (
                  | ReturnType<typeof OrganizationActions.deleteGroupSuccess>
                  | ReturnType<typeof OrganizationActions.editUsersSuccess>
                  | LoadUserInfo
                )[] = [OrganizationActions.deleteGroupSuccess({ groupId: action.groupId })];

                if (updatedUsers.length > 0) {
                  resultActions.push(OrganizationActions.editUsersSuccess({ users: updatedUsers }));
                }

                if (companyId) {
                  resultActions.push(new LoadUserInfo(companyId));
                }

                return resultActions;
              }),
            ),
          ),
          catchError((error) =>
            of(OrganizationActions.deleteGroupError({ groupId: action.groupId, error })),
          ),
        );
      }),
    ),
  );

  createUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.createUsers),
      switchMap((action) =>
        this.userService.createUsers(action.request).pipe(
          map((response) => {
            return OrganizationActions.createUsersSuccess({ response });
          }),
          catchError((error) => of(OrganizationActions.createUsersError({ error }))),
        ),
      ),
    ),
  );

  createRuleBasedTeamWithUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.createRuleBasedTeamWithUsers),
      switchMap((action) =>
        // 1. Create the rule-based sub group (without manager)
        this.groupService.createRuleBasedSubGroup(action.parentGroupId, action.group).pipe(
          switchMap((groupHttpResponse) => {
            const createdGroup = groupHttpResponse.body;
            const managerIsNew = action.managerEmail && !action.manager;

            // 2a. Create member users (as members of the new team)
            const createMembers$ =
              action.usersRequest && action.usersRequest.users.length > 0
                ? this.userService.createUsers({
                    users: action.usersRequest.users,
                    groupId: createdGroup.id,
                    hierarchyId: action.hierarchyId,
                  })
                : of(undefined as CreateUsersResponse | undefined);

            // 2b. Create manager user separately (if new)
            // If manager is also a member → groupId is createdGroup.id (belongs to team)
            // If manager is NOT a member → groupId is parentGroupId (belongs to parent/top group)
            const createManager$ = managerIsNew
              ? this.userService.createUsers({
                  users: [action.managerUserRequest ?? { email: action.managerEmail }],
                  groupId: action.managerIsAlsoMember ? createdGroup.id : action.parentGroupId,
                  hierarchyId: action.hierarchyId,
                })
              : of(undefined as CreateUsersResponse | undefined);

            return forkJoin({
              membersResponse: createMembers$,
              managerResponse: createManager$,
            }).pipe(
              // 3. getForEdit and update the group with manager (if any)
              switchMap(({ membersResponse, managerResponse }) => {
                if (!action.managerEmail) {
                  return of({
                    membersResponse,
                    managerResponse,
                    updatedGroup: undefined,
                    resolvedManager: undefined,
                  });
                }

                // Resolve manager from existing user or just-created users
                let resolvedManager: { id: number; name: string; email: string } | undefined;

                if (action.manager) {
                  resolvedManager = action.manager;
                } else {
                  const created = managerResponse?.createdUsers?.find(
                    (u) =>
                      u.email?.trim().toLowerCase() === action.managerEmail.trim().toLowerCase(),
                  );
                  if (created) {
                    resolvedManager = {
                      id: created.id,
                      name: created.fullName,
                      email: created.email,
                    };
                  }
                }

                if (!resolvedManager) {
                  return of({
                    membersResponse,
                    managerResponse,
                    updatedGroup: undefined,
                    resolvedManager: undefined,
                  });
                }

                return this.groupService.getForEdit(createdGroup.id).pipe(
                  switchMap((fullEditGroup) => {
                    const editGroup: EditGroup = {
                      ...fullEditGroup,
                      companyId: action.companyId,
                      managers: [resolvedManager.id] as any[],
                      attributes: (fullEditGroup.attributes ?? []).map((a) => ({
                        ...a,
                        AttributeDefinitionId: a.attributeId,
                      })),
                    };

                    return this.groupService.updateRuleBasedGroup(createdGroup.id, editGroup).pipe(
                      map((res) => ({
                        membersResponse,
                        managerResponse,
                        updatedGroup: res.body,
                        resolvedManager,
                      })),
                    );
                  }),
                );
              }),
              // 4. Dispatch state actions
              mergeMap(({ membersResponse, managerResponse, updatedGroup, resolvedManager }) => {
                const resultActions: (
                  | ReturnType<typeof OrganizationActions.addGroupSuccess>
                  | ReturnType<typeof OrganizationActions.createUsersSuccess>
                  | ReturnType<typeof OrganizationActions.addUsersToGroup>
                  | ReturnType<typeof OrganizationActions.editGroupSuccess>
                  | ReturnType<typeof OrganizationActions.moveUsers>
                  | ReturnType<typeof OrganizationActions.updateValidationStatus>
                  | LoadUserInfo
                )[] = [];

                // Add group to state tree
                resultActions.push(
                  OrganizationActions.addGroupSuccess({
                    hierarchyId: action.hierarchyId,
                    categoryId: action.group.categoryId,
                    parentId: action.parentGroupId,
                    group: createdGroup,
                    leaf: true,
                    manager: resolvedManager,
                    replacesGroupId: action.temporaryGroupId,
                    replacesUserIds: action.optimisticMembers?.map((member) => member.id),
                  }),
                );

                // Add newly created member users to state
                if (membersResponse) {
                  resultActions.push(
                    OrganizationActions.createUsersSuccess({ response: membersResponse }),
                  );

                  if (membersResponse.createdUsers.length > 0) {
                    resultActions.push(
                      OrganizationActions.addUsersToGroup({
                        hierarchyId: action.hierarchyId,
                        groupId: createdGroup.id,
                        users: membersResponse.createdUsers,
                      }),
                    );
                  }
                }

                // Add newly created manager user to state
                if (managerResponse) {
                  resultActions.push(
                    OrganizationActions.createUsersSuccess({ response: managerResponse }),
                  );

                  // Only add manager to the group in state if they are also a member
                  if (action.managerIsAlsoMember && managerResponse.createdUsers.length > 0) {
                    resultActions.push(
                      OrganizationActions.addUsersToGroup({
                        hierarchyId: action.hierarchyId,
                        groupId: createdGroup.id,
                        users: managerResponse.createdUsers,
                      }),
                    );
                  }
                }

                // Move existing users into the new group
                if (action.existingMemberIdsToAdd && action.existingMemberIdsToAdd.length > 0) {
                  resultActions.push(
                    OrganizationActions.moveUsers({
                      companyId: action.companyId,
                      hierarchyId: action.hierarchyId,
                      newGroupId: createdGroup.id,
                      userIds: action.existingMemberIdsToAdd,
                    }),
                  );
                }

                // Update group in state tree with manager info
                if (updatedGroup && resolvedManager) {
                  resultActions.push(
                    OrganizationActions.editGroupSuccess({
                      group: updatedGroup,
                      manager: resolvedManager,
                    }),
                  );
                }

                // Send invite/welcome email to manager — only if the trigger has a configured,
                // enabled setting, and (unless that setting opts in via AllowResend) only if they
                // haven't already received one, since an account should only ever be
                // created/welcomed once — regardless of what action.inviteManager says.
                if (action.inviteManager && resolvedManager) {
                  const managerSetting = this.findOrganisationEmailSetting(
                    OrganisationEmailTrigger.ManagerAdded,
                  );
                  if (
                    managerSetting &&
                    this.canSendOrganisationEmail(managerSetting, resolvedManager.id)
                  ) {
                    const managerId = resolvedManager.id;
                    this.findOrganisationEmail$(
                      OrganisationEmailTrigger.ManagerAdded,
                      action.companyId,
                    ).subscribe((welcomeEmail) => {
                      if (welcomeEmail) {
                        this.emailService.send(welcomeEmail.id, managerId).subscribe(() => {
                          this.store.dispatch(
                            OrganizationActions.setCommunicationSent({
                              userId: managerId,
                              communicationType: managerSetting.type,
                            }),
                          );
                        });
                      }
                    });
                  }
                }

                // Send welcome email to newly added/moved non-manager members (never to existing
                // team members — there are none yet, this is a brand-new team — and never to the
                // manager, even when they're also listed as a member). Same once-per-recipient rule
                // as the manager email, driven by the same AllowResend-aware setting.
                if (action.sendWelcomeEmail) {
                  const newMemberIds = [
                    ...(membersResponse?.createdUsers?.map((u) => u.id) ?? []),
                    ...(action.existingMemberIdsToAdd ?? []),
                  ].filter((id) => id !== resolvedManager?.id);

                  const memberSetting = this.findOrganisationEmailSetting(
                    OrganisationEmailTrigger.UserAdded,
                  );
                  const eligibleMemberIds = memberSetting
                    ? newMemberIds.filter((id) => this.canSendOrganisationEmail(memberSetting, id))
                    : [];

                  if (eligibleMemberIds.length > 0) {
                    this.findOrganisationEmail$(
                      OrganisationEmailTrigger.UserAdded,
                      action.companyId,
                    ).subscribe((welcomeEmail) => {
                      if (welcomeEmail) {
                        eligibleMemberIds.forEach((userId) =>
                          this.emailService.send(welcomeEmail.id, userId).subscribe(() => {
                            this.store.dispatch(
                              OrganizationActions.setCommunicationSent({
                                userId,
                                communicationType: memberSetting.type,
                              }),
                            );
                          }),
                        );
                      }
                    });
                  }
                }

                // Reset validationStatus so it doesn't interfere with dialogs
                // that subscribe to it (e.g. UserComponent auto-closes on 'valid')
                resultActions.push(
                  OrganizationActions.updateValidationStatus({
                    validationStatus: ValidationStatusEnum.idle,
                  }),
                  new LoadUserInfo(action.companyId),
                );

                return resultActions;
              }),
            );
          }),
          catchError((error) => {
            if (error?.error?.error === 'A group with the selected external ID already exists.') {
              return of(
                OrganizationActions.createRuleBasedTeamWithUsersError({
                  error: error?.error?.error,
                }),
              );
            }
            return of(OrganizationActions.createRuleBasedTeamWithUsersError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteUsers),
      switchMap((action) =>
        this.userService.GDPRDeleteUsers(action.companyId, action.model).pipe(
          map((usersHttpResponse) => {
            return OrganizationActions.deleteUsersSuccess({
              userIds: usersHttpResponse.body,
              hierarchyId: action.model.hierarchyId,
            });
          }),
          catchError((error) => {
            return of(OrganizationActions.deleteUsersError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteManagerFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteManagerFromTeam),
      switchMap((action) => {
        const noOneManager = { id: 0, name: '', email: '' };

        const updateTeams$ =
          action.teamsToUnmanage.length > 0
            ? forkJoin(
                action.teamsToUnmanage.map((group) =>
                  this.groupService.update(group.id, group).pipe(
                    map((res) =>
                      OrganizationActions.editGroupSuccess({
                        group: res.body,
                        manager: noOneManager,
                        currentManagerId: action.currentManagerId,
                      }),
                    ),
                  ),
                ),
              )
            : of([] as ReturnType<typeof OrganizationActions.editGroupSuccess>[]);

        return updateTeams$.pipe(
          switchMap((editGroupActions) => [
            ...editGroupActions,
            OrganizationActions.deleteUsers({
              companyId: action.companyId,
              model: {
                companyId: action.companyId,
                hierarchyId: action.hierarchyId,
                userIds: [action.userId],
              },
            }),
          ]),
          catchError((error) => of(OrganizationActions.deleteUsersError({ error }))),
        );
      }),
    ),
  );

  editRuleBasedTeamWithUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editRuleBasedTeamWithUsers),
      withLatestFrom(this.store.select((state) => state.organizationState.users)),
      switchMap(([action, users]) => {
        const editGroup: EditGroup = {
          ...action.group,
          attributes: (action.group.attributes ?? []).map((a) => ({
            ...a,
            AttributeDefinitionId: a.attributeId,
          })),
        };

        return this.groupService.updateRuleBasedGroup(action.group.id, editGroup).pipe(
          map((groupHttpResponse) => {
            const managerIds = action.group.managers as any as number[];
            const managerId = managerIds?.[0];
            const managerUser = managerId ? users[managerId] : undefined;
            const manager = managerUser
              ? {
                  id: managerUser.id,
                  name: managerUser.fullName ?? '',
                  email: managerUser.email ?? '',
                }
              : { id: 0, name: '', email: '' };

            return OrganizationActions.editGroupSuccess({
              group: groupHttpResponse.body,
              manager,
              currentManagerId: action.currentManagerId,
            });
          }),
          catchError((error) => of(OrganizationActions.editGroupError({ error }))),
        );
      }),
    ),
  );

  checkCommunicationSentStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.checkCommunicationSentStatuses),
      switchMap(() =>
        this.userService.checkCommunicationSent().pipe(
          map((statuses) =>
            OrganizationActions.checkCommunicationSentStatusesSuccess({ statuses }),
          ),
          catchError((error) =>
            of(OrganizationActions.checkCommunicationSentStatusesError({ error })),
          ),
        ),
      ),
    ),
  );

  importUsersToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.importUsersToTeam),
      mergeMap((action) => {
        const tempUserIds = (action.newMembers ?? []).map((u) => u.id);

        // Create the placeholder members server-side.
        const createMembers$ =
          action.newMembers && action.newMembers.length > 0
            ? this.userService.createUsers({
                users: action.newMembers.map((u) => ({
                  email: u.email ?? '',
                  firstName: u.firstName,
                  lastName: u.lastName,
                  language: u.language,
                })),
                groupId: action.groupId,
                hierarchyId: action.hierarchyId,
              })
            : of({ createdUsers: [], skippedEmails: [] } as CreateUsersResponse);

        // Move the existing members into the team.
        const moveMembers$ =
          action.existingMemberIdsToAdd && action.existingMemberIdsToAdd.length > 0
            ? this.userService.move({
                companyId: action.companyId,
                hierarchyId: action.hierarchyId,
                newGroupId: action.groupId,
                userIds: action.existingMemberIdsToAdd,
              })
            : of([] as User[]);

        return forkJoin([createMembers$, moveMembers$]).pipe(
          tap(([createResponse, movedUsers]) => {
            if (!action.sendWelcomeEmail) return;

            const newMemberIds = [
              ...createResponse.createdUsers.map((u) => u.id),
              ...movedUsers.map((u) => u.id),
            ];
            if (newMemberIds.length === 0) return;

            const memberSetting = this.findOrganisationEmailSetting(
              OrganisationEmailTrigger.UserAdded,
            );
            const eligibleMemberIds = memberSetting
              ? newMemberIds.filter((id) => this.canSendOrganisationEmail(memberSetting, id))
              : [];
            if (eligibleMemberIds.length === 0) return;

            this.findOrganisationEmail$(
              OrganisationEmailTrigger.UserAdded,
              action.companyId,
            ).subscribe((welcomeEmail) => {
              if (welcomeEmail) {
                eligibleMemberIds.forEach((userId) =>
                  this.emailService.send(welcomeEmail.id, userId).subscribe(() => {
                    this.store.dispatch(
                      OrganizationActions.setCommunicationSent({
                        userId,
                        communicationType: memberSetting.type,
                      }),
                    );
                  }),
                );
              }
            });
          }),
          map(([createResponse, movedUsers]) => {
            const count = createResponse.createdUsers.length + movedUsers.length;
            this.toaster.confirmed(
              this.translations.translate(
                'INSIGHT.TOOLS.ORGANIZATIONSECTION.MEMBERS_ADDED_SUCCESS',
                { count },
              ),
            );
            return OrganizationActions.importUsersToTeamSuccess({
              hierarchyId: action.hierarchyId,
              groupId: action.groupId,
              tempUserIds,
              createdUsers: createResponse.createdUsers,
              movedUsers,
            });
          }),
          catchError((error) => {
            this.toaster.error(
              this.translations.translate('INSIGHT.TOOLS.ORGANIZATIONSECTION.MEMBERS_ADDED_ERROR'),
            );
            return of(OrganizationActions.importUsersToTeamError({ error }));
          }),
        );
      }),
    ),
  );
}
